import storageService from '%app/js/common/services/storageService';
import {
    APPEARANCE_SCHEME_ATTRIBUTE,
    APPEARANCE_STORAGE_KEY,
    APPEARANCE_STORED_KEY,
    DEFAULT_APPEARANCE_CHOICE,
    SCHEME_LIGHT,
    normalizeAppearanceChoice,
} from './appearanceSchemes';
import resolveAppearanceScheme, { DEVICE_PREFERENCE_QUERIES, readDevicePreferences } from './resolveAppearanceScheme';
import applyAppearanceToDocument from './applyAppearanceToDocument';

const DISABLED_SNAPSHOT = Object.freeze({
    enabled: false,
    choice: DEFAULT_APPEARANCE_CHOICE,
    scheme: SCHEME_LIGHT,
});

function watchMediaQuery(mediaQueryList, onChange) {
    if (typeof mediaQueryList.addEventListener === 'function') {
        mediaQueryList.addEventListener('change', onChange);

        return () => mediaQueryList.removeEventListener('change', onChange);
    }

    mediaQueryList.addListener(onChange);

    return () => mediaQueryList.removeListener(onChange);
}

export function createAppearanceStore(environment) {
    const listeners = new Set();
    let snapshot = null;
    let stopWatching = null;

    const matchMedia = typeof environment.matchMedia === 'function'
        ? query => environment.matchMedia(query)
        : null;

    const isEnabled = () => environment.document.documentElement.hasAttribute(APPEARANCE_SCHEME_ATTRIBUTE);

    const readStoredChoice = () => {
        try {
            return normalizeAppearanceChoice(storageService.getData(APPEARANCE_STORAGE_KEY, DEFAULT_APPEARANCE_CHOICE));
        } catch (error) {
            return DEFAULT_APPEARANCE_CHOICE;
        }
    };

    const createSnapshot = choice => Object.freeze({
        enabled: true,
        choice,
        scheme: resolveAppearanceScheme(choice, readDevicePreferences(matchMedia)),
    });

    const ensureSnapshot = () => {
        if (snapshot === null) {
            snapshot = createSnapshot(readStoredChoice());
        }

        return snapshot;
    };

    const publish = (choice) => {
        const current = ensureSnapshot();
        const next = createSnapshot(choice);
        if (next.choice === current.choice && next.scheme === current.scheme) {
            return;
        }

        snapshot = next;
        applyAppearanceToDocument(environment.document, snapshot.scheme);
        listeners.forEach(listener => listener());
    };

    const handleStorage = (event) => {
        if (event.key === APPEARANCE_STORED_KEY || event.key === null) {
            publish(readStoredChoice());
        }
    };

    const handleDeviceChange = () => publish(snapshot.choice);

    const startWatching = () => {
        const stopMediaWatchers = matchMedia === null
            ? []
            : DEVICE_PREFERENCE_QUERIES.map(query => watchMediaQuery(matchMedia(query), handleDeviceChange));
        environment.addEventListener('storage', handleStorage);

        return () => {
            stopMediaWatchers.forEach(stop => stop());
            environment.removeEventListener('storage', handleStorage);
        };
    };

    const getSnapshot = () => (isEnabled() ? ensureSnapshot() : DISABLED_SNAPSHOT);

    const subscribe = (listener) => {
        if (!isEnabled()) {
            return () => {};
        }

        ensureSnapshot();
        listeners.add(listener);
        if (stopWatching === null) {
            stopWatching = startWatching();
        }

        return () => {
            listeners.delete(listener);
            if (listeners.size === 0 && stopWatching !== null) {
                stopWatching();
                stopWatching = null;
            }
        };
    };

    const setChoice = (choice) => {
        if (!isEnabled()) {
            return;
        }

        const normalizedChoice = normalizeAppearanceChoice(choice);
        storageService.setData(APPEARANCE_STORAGE_KEY, normalizedChoice);
        publish(normalizedChoice);
    };

    return {
        getSnapshot,
        subscribe,
        setChoice,
    };
}

export default createAppearanceStore(window);
